import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../shared/providers/LanguageProvider';
import { fetchMerchantAnalytics } from '../../services/merchantService';
import { ShimmerStats, ShimmerCard } from '../../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const MerchantDashboard = () => {
  const { userProfile, session } = useAuth();
  const { t } = useLanguage();
  const [analytics, setAnalytics] = useState({
    totalPaymentsReceived: 0,
    totalDiscountAmount: 0,
    totalPaymentsCount: 0,
    totalProductsCount: 0
  });
  const [branches, setBranches] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
    fetchBranchesAndDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.token, session?.access_token]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }
      
      const data = await fetchMerchantAnalytics(token);
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranchesAndDevices = async () => {
    try {
      const token = userProfile?.token || session?.access_token;
      if (!token) return;

      const [branchesRes, devicesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/branches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/devices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const [branchesData, devicesData] = await Promise.all([
        branchesRes.json(),
        devicesRes.json()
      ]);

      if (branchesRes.ok && branchesData.success) {
        setBranches(branchesData.data || []);
      }
      if (devicesRes.ok && devicesData.success) {
        setDevices(devicesData.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches and devices:', error);
    }
  };

  const quickActions = [
    {
      title: t('startSale'),
      description: t('createNewPaymentSession'),
      icon: '💳',
      onClick: () => navigate('/payment-sessions')
    },
    {
      title: t('addProduct'),
      description: t('addNewProductToCatalog'),
      icon: '📦',
      onClick: () => navigate('/products')
    },
    {
      title: t('viewOffers'),
      description: t('viewProductOffers'),
      icon: '🎫',
      onClick: () => navigate('/offers')
    },
    {
      title: t('manageDevices'),
      description: t('configurePaymentDevices'),
      icon: '📱',
      onClick: () => navigate('/devices')
    },
    {
      title: t('manageBranches'),
      description: t('configureBusinessBranches'),
      icon: '🏢',
      onClick: () => navigate('/branches')
    }
  ];

  const metrics = [
    {
      title: t('totalPaymentsReceived'),
      value: `SAR ${analytics.totalPaymentsReceived.toFixed(2)}`,
      icon: '💰'
    },
    {
      title: t('totalDiscountsGiven'),
      value: `SAR ${analytics.totalDiscountAmount.toFixed(2)}`,
      icon: '🎫'
    },
    {
      title: t('totalPayments'),
      value: analytics.totalPaymentsCount,
      icon: '📊'
    },
    {
      title: t('totalProducts'),
      value: analytics.totalProductsCount,
      icon: '📦'
    }
  ];

  const statusVariant = (status) => {
    if (status === 'active') return 'success';
    if (status === 'inactive') return 'destructive';
    return 'warning';
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <div className="h-8 w-64 bg-muted rounded mb-2" />
            <div className="h-4 w-96 bg-muted rounded" />
          </div>
          <div className="h-10 w-24 bg-muted rounded" />
        </div>
        <ShimmerStats count={4} />
        <div>
          <div className="h-6 w-32 bg-muted rounded mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <ShimmerCard key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('merchantDashboardTitle')}
        description={t('merchantDashboardSubtitle')}
        actions={
          <Button onClick={fetchAnalytics}>{t('refresh')}</Button>
        }
      />

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6 text-destructive">{error}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <Card key={index}>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{metric.title}</p>
                <p className="text-2xl font-bold text-foreground">{metric.value}</p>
              </div>
              <div className="text-3xl">{metric.icon}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="text-xl font-semibold text-foreground mb-4">{t('branchOverview')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('totalBranches')}</p>
                <p className="text-2xl font-bold text-foreground">{branches.length}</p>
              </div>
              <div className="text-3xl">🏢</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('activeBranches')}</p>
                <p className="text-2xl font-bold text-foreground">
                  {branches.filter(b => b.status === 'active').length}
                </p>
              </div>
              <div className="text-3xl">✅</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('totalDevices')}</p>
                <p className="text-2xl font-bold text-foreground">{devices.length}</p>
              </div>
              <div className="text-3xl">📱</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('defaultDevices')}</p>
                <p className="text-2xl font-bold text-foreground">
                  {devices.filter(d => d.is_default).length}
                </p>
              </div>
              <div className="text-3xl">⭐</div>
            </CardContent>
          </Card>
        </div>

        {branches.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('branchSummary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {branches.map(branch => {
                const branchDevices = devices.filter(d => d.branch_id === branch.id);
                const defaultDevice = branchDevices.find(d => d.is_default);
                
                return (
                  <div
                    key={branch.id}
                    className="flex items-center justify-between rounded-lg border bg-muted/40 p-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-2xl">🏢</div>
                      <div>
                        <h4 className="font-semibold text-foreground">{branch.branch_name}</h4>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {branch.branch_code && <div>{t('code')}: {branch.branch_code}</div>}
                          {branch.address && <div>📍 {branch.address}</div>}
                          {branch.manager_name && <div>👤 {t('manager')}: {branch.manager_name}</div>}
                          <div>📱 {t('devicesLabel')}: {branchDevices.length}</div>
                          {defaultDevice && (
                            <div className="text-emerald-600 dark:text-emerald-400">
                              ⭐ {t('defaultLabel')}: {defaultDevice.device_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge variant={statusVariant(branch.status)}>{branch.status}</Badge>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      <div>
        <h2 className="text-xl font-semibold text-foreground mb-4">{t('quickActions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Card
              key={index}
              className="cursor-pointer transition-colors hover:bg-accent"
              onClick={action.onClick}
            >
              <CardHeader>
                <div className="text-3xl mb-1">{action.icon}</div>
                <CardTitle className="text-lg">{action.title}</CardTitle>
                <CardDescription>{action.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('recentActivityMerchant')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            {t('noRecentActivity')} {t('startByCreatingFirstSale')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MerchantDashboard;
